import { useMemo } from "react";

const COMMON_PASSWORDS = [
  "password", "abc123", "123456", "qwerty", "letmein", "welcome",
  "monkey", "dragon", "master", "1234567", "12345678", "123456789",
  "1234567890", "password1", "iloveyou", "sunshine", "princess",
  "football", "charlie", "shadow", "michael", "111111", "000000",
];

export function getPasswordError(password: string): string | null {
  if (password.length > 0 && password.length < 8) {
    return "Password must be at least 8 characters.";
  }
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
    return "This password is too common. Please choose a more unique one.";
  }
  return null;
}

function getStrength(password: string): { level: number; label: string } {
  if (!password) return { level: 0, label: "" };
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) return { level: 1, label: "Weak" };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { level: 1, label: "Weak" };
  if (score === 2) return { level: 2, label: "Fair" };
  if (score === 3) return { level: 3, label: "Good" };
  return { level: 4, label: "Strong" };
}

const BAR_COLORS = ["", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500"];

export default function PasswordStrengthMeter({ password }: { password: string }) {
  const { level, label } = useMemo(() => getStrength(password), [password]);
  const error = useMemo(() => getPasswordError(password), [password]);

  if (!password) {
    return (
      <p className="text-xs text-muted-foreground mt-1">
        Must be at least 8 characters and not a commonly used password.
      </p>
    );
  }

  return (
    <div className="mt-1 space-y-1">
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Must be at least 8 characters and not a commonly used password.
        </p>
      )}
      <div className="flex items-center gap-2">
        <div className="flex gap-1 flex-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= level ? BAR_COLORS[level] : "bg-muted"
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-medium ${
          level <= 1 ? "text-red-600" : level === 2 ? "text-orange-600" : level === 3 ? "text-yellow-600" : "text-green-600"
        }`}>
          {label}
        </span>
      </div>
    </div>
  );
}
